import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
    CheckIcon,
    MagnifyingGlassIcon,
    SpeakerXMarkIcon,
    XMarkIcon,
} from '@heroicons/react/20/solid';
import { useLiveClassState } from '../../../hooks/liveClassStore';
import {
    muteParticipant,
    askToUnmuteParticipant,
    muteAllStudents,
} from '../../../hooks/useVideoSession';
import { clearParticipantHand } from '../../../hooks/useLiveClassSignals';
import { canMuteOthers } from '../../../utils/liveClassPermissions';
import { Tooltip } from '../../../components/smartTable/Tooltip';
import { LiveParticipantCard } from './LiveParticipantCard';
import './live-participants.css';

const normalize = (value) => String(value || '').trim().toLowerCase();

/**
 * Orden por urgencia: primero quien pide la palabra, luego quien habla, luego
 * el profesor y al final alfabético.
 */
const buildSorter = (raisedHands, speaking) => (a, b) => {
    const handA = raisedHands.includes(a.userId) ? 0 : 1;
    const handB = raisedHands.includes(b.userId) ? 0 : 1;
    if (handA !== handB) return handA - handB;

    const speakA = speaking.includes(a.userId) ? 0 : 1;
    const speakB = speaking.includes(b.userId) ? 0 : 1;
    if (speakA !== speakB) return speakA - speakB;

    const hostA = a.isHost || a.isManager ? 0 : 1;
    const hostB = b.isHost || b.isManager ? 0 : 1;
    if (hostA !== hostB) return hostA - hostB;

    return a.displayName.localeCompare(b.displayName, 'es');
};

/**
 * Tab «En vivo»: solo los usuarios conectados a la videollamada.
 * El tab «Participantes» sigue mostrando a todo el alumnado del curso.
 */
export const LiveParticipantsPanel = () => {
    const participants = useLiveClassState((s) => s.participants);
    const speakingUserIds = useLiveClassState((s) => s.speakingUserIds);
    const raisedHandUserIds = useLiveClassState((s) => s.raisedHandUserIds);
    const networkQuality = useLiveClassState((s) => s.networkQuality);
    const isHost = useLiveClassState((s) => s.isHost);
    const connection = useLiveClassState((s) => s.connection);

    const [query, setQuery] = useState('');
    const [busyUserId, setBusyUserId] = useState(null);
    const [mutingAll, setMutingAll] = useState(false);

    const canManage = canMuteOthers({ isHost });

    const visible = useMemo(() => {
        const needle = normalize(query);
        const filtered = needle
            ? participants.filter((p) => normalize(p.displayName).includes(needle))
            : participants;
        return [...filtered].sort(buildSorter(raisedHandUserIds, speakingUserIds));
    }, [participants, query, raisedHandUserIds, speakingUserIds]);

    const handsCount = raisedHandUserIds.length;
    const openMics = participants.filter((p) => !p.isHost && !p.isSelf && !p.muted).length;

    const runAction = useCallback(async (userId, action) => {
        setBusyUserId(userId);
        try {
            await action();
        } catch (err) {
            toast.error(err?.message || 'No se pudo completar la acción');
        } finally {
            setBusyUserId(null);
        }
    }, []);

    const handleMute = useCallback((userId) => runAction(userId, async () => {
        await muteParticipant(userId);
    }), [runAction]);

    const handleAskUnmute = useCallback((userId) => runAction(userId, async () => {
        await askToUnmuteParticipant(userId);
        toast.success('Le pedimos al alumno que active su micrófono');
    }), [runAction]);

    const handleLowerHand = useCallback((userId) => runAction(userId, async () => {
        await clearParticipantHand(userId);
    }), [runAction]);

    const handleMuteAll = useCallback(async () => {
        setMutingAll(true);
        try {
            const muted = await muteAllStudents();
            toast.success(
                muted
                    ? `Silenciaste a ${muted} ${muted === 1 ? 'estudiante' : 'estudiantes'}`
                    : 'Los estudiantes ya estaban silenciados',
            );
        } catch (err) {
            toast.error(err?.message || 'No se pudo silenciar a todos');
        } finally {
            setMutingAll(false);
        }
    }, []);

    return (
        <div className="live-panel">
            <div className="live-panel__head">
                <p className="live-panel__count">
                    {participants.length}
                    {participants.length === 1 ? ' conectado' : ' conectados'}
                    {handsCount > 0 ? ` · ${handsCount} con la mano levantada` : ''}
                </p>

                {canManage ? (
                    <Tooltip
                        title={openMics
                            ? 'Silencia a los estudiantes sin afectar tu micrófono'
                            : 'Ningún estudiante tiene el micrófono abierto'}
                        placement="bottom"
                    >
                        <button
                            type="button"
                            className={`live-panel__mute-all${openMics ? '' : ' is-idle'}`}
                            disabled={mutingAll || !openMics}
                            aria-busy={mutingAll}
                            onClick={handleMuteAll}
                        >
                            {openMics ? (
                                <SpeakerXMarkIcon className="live-panel__mute-all-icon" aria-hidden="true" />
                            ) : (
                                <CheckIcon className="live-panel__mute-all-icon" aria-hidden="true" />
                            )}
                            <span>
                                {mutingAll
                                    ? 'Silenciando…'
                                    : openMics ? 'Silenciar a todos' : 'Todos en silencio'}
                            </span>
                            {openMics && !mutingAll ? (
                                <span
                                    className="live-panel__mute-all-count"
                                    aria-label={`${openMics} ${openMics === 1 ? 'micrófono abierto' : 'micrófonos abiertos'}`}
                                >
                                    {openMics}
                                </span>
                            ) : null}
                        </button>
                    </Tooltip>
                ) : null}
            </div>

            <div className="live-panel__search">
                <MagnifyingGlassIcon className="live-panel__search-icon" aria-hidden="true" />
                <input
                    type="search"
                    className="live-panel__search-input"
                    placeholder="Buscar usuario en vivo..."
                    aria-label="Buscar usuario en vivo"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                />
                {query ? (
                    <button
                        type="button"
                        className="live-panel__search-clear"
                        aria-label="Limpiar búsqueda"
                        onClick={() => setQuery('')}
                    >
                        <XMarkIcon aria-hidden="true" />
                    </button>
                ) : null}
            </div>

            <div className={`live-panel__grid${visible.length === 1 ? ' is-single' : visible.length > 1 ? ' is-many' : ''}`}>
                {connection === 'reconnecting' ? (
                    <p className="live-panel__notice">
                        Reconectando con la videollamada…
                    </p>
                ) : null}

                {visible.length === 0 ? (
                    <p className="live-panel__empty">
                        {participants.length === 0
                            ? 'Nadie más está conectado a la clase en vivo.'
                            : 'Ningún usuario en vivo coincide con la búsqueda.'}
                    </p>
                ) : (
                    visible.map((participant) => (
                        <LiveParticipantCard
                            key={participant.userId}
                            participant={participant}
                            isSpeaking={speakingUserIds.includes(participant.userId)}
                            handRaised={raisedHandUserIds.includes(participant.userId)}
                            networkQuality={networkQuality[participant.userId] || null}
                            canManage={canManage}
                            busy={busyUserId === participant.userId}
                            onMute={handleMute}
                            onAskUnmute={handleAskUnmute}
                            onLowerHand={handleLowerHand}
                        />
                    ))
                )}
            </div>
        </div>
    );
};
